import {
  DEFAULT_LINK_CLASSES,
  FOCUS_RING,
  LINK_BLUE_CLASSES,
  LINK_GRAY_CLASSES,
  LINK_UNDERLINE_CLASSES,
} from "@packages/design-system/constants";
import { cn } from "@packages/lib/utils";
import NextLink, { LinkProps as NextLinkProps } from "next/link";
import { AnchorHTMLAttributes } from "react";

interface LinkProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
> {
  href: NextLinkProps["href"];
  children: React.ReactNode;
  isGray?: boolean;
  noDefaultColor?: boolean;
  "aria-label"?: string;
  underline?: boolean;
  prefetch?: boolean;
}

// Helper to check if URL is a jaquishbiomedical.com subdomain
function isJaquishBiomedicalDomain(url: string): boolean {
  try {
    const urlObj = new URL(url.startsWith("//") ? `https:${url}` : url);
    return (
      urlObj.hostname === "jaquishbiomedical.com" ||
      urlObj.hostname.endsWith(".jaquishbiomedical.com")
    );
  } catch {
    return false;
  }
}

// Helper to add UTM parameters to external URLs
function addUTMParams(url: string): string {
  try {
    const urlObj = new URL(url.startsWith("//") ? `https:${url}` : url);
    urlObj.searchParams.set("utm_medium", "referrer");
    urlObj.searchParams.set("utm_source", "jaquishbiomedical.com");
    return urlObj.toString();
  } catch {
    return url;
  }
}

// Helper to compute the full className
function getLinkClasses({
  className,
  isGray,
  noDefaultColor,
  underline,
}: Pick<LinkProps, "className" | "isGray" | "noDefaultColor" | "underline">) {
  return cn(
    DEFAULT_LINK_CLASSES,
    FOCUS_RING,
    !noDefaultColor && (isGray ? LINK_GRAY_CLASSES : LINK_BLUE_CLASSES),
    underline && LINK_UNDERLINE_CLASSES,
    className,
  );
}

export default function Link({
  href,
  children,
  className,
  isGray = false,
  noDefaultColor = false,
  "aria-label": ariaLabel,
  underline = false,
  prefetch,
  ...props
}: LinkProps) {
  // Infer external links by href
  const isExternal =
    typeof href === "string" &&
    (href.startsWith("http://") ||
      href.startsWith("https://") ||
      href.startsWith("//"));

  // Handle mailto: and tel: links as plain <a>
  const isMailOrTel =
    typeof href === "string" &&
    (href.startsWith("mailto:") || href.startsWith("tel:"));

  if (isExternal || isMailOrTel) {
    const hrefString = href as string;
    const isJaquishDomain = isExternal && isJaquishBiomedicalDomain(hrefString);
    const finalHref =
      isExternal && !isJaquishDomain ? addUTMParams(hrefString) : hrefString;

    return (
      <a
        href={finalHref}
        className={getLinkClasses({
          className,
          isGray,
          noDefaultColor,
          underline,
        })}
        aria-label={ariaLabel}
        {...(isExternal && !isJaquishDomain && { rel: "noopener noreferrer" })}
        {...props}
        target={isExternal ? "_blank" : undefined}
      >
        {children}
      </a>
    );
  }

  // Internal links use Next.js <Link>
  return (
    <NextLink
      href={href}
      className={getLinkClasses({
        className,
        isGray,
        noDefaultColor,
        underline,
      })}
      aria-label={ariaLabel}
      prefetch={prefetch}
      {...props}
    >
      {children}
    </NextLink>
  );
}
